/* Number and date formatting, Indian conventions throughout.
 *
 * Rupee figures at mill scale run to crores, so `big` switches notation
 * rather than printing eleven digits nobody can read at a glance. */

const IN = 'en-IN'

export function num(v: number | null | undefined, d = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return v.toLocaleString(IN, { minimumFractionDigits: d, maximumFractionDigits: d })
}

export function inr(v: number | null | undefined, d = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  const sign = v < 0 ? '−' : ''
  return sign + '₹' + Math.abs(v).toLocaleString(IN, { minimumFractionDigits: d, maximumFractionDigits: d })
}

export function crore(v: number | null | undefined, d = 2): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return (v < 0 ? '−₹' : '₹') + (Math.abs(v) / 1e7).toFixed(d) + ' Cr'
}

/** Crores past a crore, plain rupees below — keeps tables scannable. */
export function big(v: number | null | undefined, d = 2): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return Math.abs(v) >= 1e7 ? crore(v, d) : inr(v)
}

export function kg(v: number | null | undefined, d = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return num(v, d) + ' kg'
}

export function tonnes(v: number | null | undefined, d = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return num(v / 1000, d) + ' t'
}

export function pct(v: number | null | undefined, d = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return v.toFixed(d) + '%'
}

export function signedPct(v: number | null | undefined, d = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return (v >= 0 ? '+' : '') + v.toFixed(d) + '%'
}

/** Percentage change from `from` to `to`. */
export function change(to: number | null, from: number | null, d = 1): string {
  if (to === null || from === null || !from) return '–'
  return signedPct(((to - from) / from) * 100, d)
}

export function compactKg(v: number | null | undefined, d = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return (v / 1000).toFixed(d) + 'k'
}

/** Format by the tag the API attaches to a sweepable input. */
export function byFmt(kind: string, v: number): string {
  switch (kind) {
    case 'int': return String(Math.round(v))
    case 'pct': return Math.round(v) + '%'
    case 'grams': return Math.round(v) + ' g'
    case 'crore': return '₹' + (v / 1e7).toFixed(2) + ' Cr'
    case 'inr': return inr(v)
    default: return num(v)
  }
}

// ------------------------------------------------------------------ dates
export function dayOfMonth(iso: string): string {
  return iso.split('-')[2]
}

export function shortDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(IN, { day: 'numeric', month: 'short' })
}

export function longDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(IN, {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

export function monthName(key: string, long = false): string {
  return new Date(key + '-01T00:00:00').toLocaleDateString(IN, {
    month: long ? 'long' : 'short',
    year: long ? 'numeric' : '2-digit',
  })
}

export function truncate(s: string | null | undefined, n: number): string {
  if (!s) return '–'
  return s.length > n ? s.slice(0, n - 1) + '…' : s
}
